import {PUNISHMENT_KEYS} from "@/api/punishmentApi";
import EditIcon from "@/assets/colorable/edit.svg?react";
import InfoNegIcon from "@/assets/colorable/info_neg.svg?react";
import Loader from "@/components/Common/kpi/Loader";
import {Tooltip} from "@/components/Common/Tooltip";
import DisciplineMark from "@/components/discipline/DisciplineMark";
import type {PunishmentDetailSerializer, PunishmentSerializer} from "@/generated/DjangoTypes";
import useCountdown from "@/hooks/useCountdown";
import {useDiscipline} from "@/hooks/useDiscipline";
import {useGenericInfiniteQuery, useGenericQuery} from "@/hooks/useQueryHooks";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import type {TPunishmentType} from "@/store/types";
import {getDisciplineColor, getRemainingTime, isUnverifiedType} from "@/utils/disciplineUtils";
import {useEffect, useId, type FC} from "react";
import {useTranslation} from "react-i18next";

const TimerDisciplineMark: FC<{
  className?: string;
  discipline?: PunishmentSerializer | PunishmentDetailSerializer;
  instagramUnverificationId?: number;
  onClick?: () => void;
  tiktokUnverificationId?: number;
  type: TPunishmentType | "GENERAL_UNVERIFIED";
  ugcUnverificationId?: number;
  userId?: number;
}> = ({
  className,
  discipline,
  instagramUnverificationId,
  onClick,
  tiktokUnverificationId,
  type,
  ugcUnverificationId,
  userId,
}) => {
  const {t} = useTranslation(["influencer_discipline", "common"]);
  const {getLabelByDisciplineType, invalidateQueries} = useDiscipline();

  const {remainingDays, remainingHours, remainingMinutes} = getRemainingTime(discipline?.expiration_date);

  const {countdown: minutesCountdown, isCountdownFinished} = useCountdown({
    // Add 2 extra minutes to the countdown to delay the refetch slightly, since the backend might not update immediately when time runs out.
    // We only apply this delay if less than 15 minutes remain, as it's unnecessary for longer durations.
    from: remainingHours === 0 && remainingMinutes < 15 ? remainingMinutes + 2 : remainingMinutes,
    interval: 60,
    shouldStartCountdown: remainingDays === 0,
  });

  useEffect(() => {
    if (isCountdownFinished && remainingHours === 0) {
      invalidateQueries();
    }
  }, [invalidateQueries, isCountdownFinished, remainingHours]);

  const id = useId();

  const punishmentQuery = useGenericQuery<PunishmentDetailSerializer>({
    queryKey: PUNISHMENT_KEYS.qPunishment(discipline?.id!),
    enabled: false,
  });

  const allPunishmentsQuery = useGenericInfiniteQuery<PunishmentDetailSerializer>({
    queryKey: PUNISHMENT_KEYS.qPunishments(userId!),
    enabled: !!userId && type === "GENERAL_UNVERIFIED",
  });

  const campaignApplication =
    typeof discipline?.campaign_application === "number"
      ? punishmentQuery.data?.campaign_application
      : discipline?.campaign_application;

  const getWhatIsSectionText = () => {
    switch (type) {
      case "PERMANENT_BAN":
        return t("what_is_ban_text");
      case "PROBATION":
        return t("what_is_probation_text");
      case "TIMED_BAN":
        return t("what_is_timed_ban_text");
      default:
        return "";
    }
  };

  const getPunishmentDetails = () => {
    if (type === "GENERAL_UNVERIFIED") {
      const unverifications = allPunishmentsQuery.data?.filter(
        punishment =>
          punishment.id === instagramUnverificationId ||
          punishment.id === tiktokUnverificationId ||
          punishment.id === ugcUnverificationId,
      );

      return unverifications?.map(punishment => ({
        body: punishment.body,
        campaignTitle: punishment.campaign_application?.campaign?.title,
        id: punishment?.id,
        title: punishment.title,
        type: punishment.punishment_type,
      }));
    }

    return [
      {
        body: discipline?.body,
        campaignTitle: campaignApplication?.campaign.title,
        id: discipline?.id,
        title: discipline?.title,
        type: discipline?.punishment_type,
      },
    ];
  };
  const punishmentDetails = getPunishmentDetails();

  return (
    <button
      className={clsxm(
        "flex h-8 w-fit items-center gap-2 rounded-md px-3 text-white",
        className,
        !onClick && "cursor-default",
      )}
      onClick={e => {
        e.stopPropagation();
        onClick?.();
      }}
      style={{backgroundColor: `var(--color-${getDisciplineColor(type)})`}}>
      <DisciplineMark className="size-4" type={type} />
      {discipline?.expiration_date && remainingMinutes > 0 && (
        <span className="text-sm">
          {remainingDays > 0
            ? `${remainingDays}${t("common:days_abbrevation")}`
            : `${remainingHours.toString().padStart(2, "0")}:${minutesCountdown.toString().padStart(2, "0")}`}
        </span>
      )}
      <Can I="create" a="DisciplinaryOperation">
        <EditIcon className="size-3.5" />
      </Can>
      <div data-tooltip-id={`discipline-tooltip-${id}`}>
        <InfoNegIcon className="size-3.5" style={{color: `var(--color-${getDisciplineColor(type)})`}} />
      </div>
      <Tooltip
        className="border-neutral-white-dark z-100 border p-3 shadow-md"
        classNameArrow="shadow-md"
        clickable
        id={`discipline-tooltip-${id}`}
        opacity={1}
        delayShow={200}
        place="bottom"
        variant="light"
        afterShow={() => {
          if (typeof discipline?.campaign_application === "number" && !punishmentQuery.isFetched) {
            punishmentQuery.refetch();
          }
        }}>
        <div className="flex max-w-72 flex-col gap-2.5">
          {punishmentQuery.isFetching && <Loader styles={{loaderClassName: "size-6 align-center"}} />}
          {punishmentDetails?.map(punishment => (
            <div className="flex flex-col gap-1" key={punishment.id}>
              <div className="flex items-center gap-2">
                <DisciplineMark type={punishment.type!} variant="outline" className="size-3" />
                <span className="text-neutral-black text-sm font-semibold">{punishment?.title}</span>
              </div>
              <span className="text-neutral-black text-left text-xs">{punishment.campaignTitle}</span>
              <div className="flex max-h-20 overflow-y-auto">
                <span className="text-neutral-grey-dark text-2xs text-left">{punishment?.body}</span>
              </div>
              {!isUnverifiedType(type as TPunishmentType) && type !== "GENERAL_UNVERIFIED" && (
                <>
                  <div className="bg-neutral-grey-light-dark my-1.5 h-px w-full" />
                  <span className="text-2xs text-left font-semibold">
                    {t("what_is", {punishment: getLabelByDisciplineType(type as TPunishmentType).toLowerCase()})}
                  </span>
                  <span className="text-neutral-grey-dark text-2xs text-left">{getWhatIsSectionText()}</span>
                </>
              )}
            </div>
          ))}
        </div>
      </Tooltip>
    </button>
  );
};

export default TimerDisciplineMark;
